import React, { useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, Search, Trash2 } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { funnelStagePresentation } from './funnelStageUtils';

const Stacked = ({ top, bottom, topBold = true, topColor, bottomColor, mutedColor }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topBold ? 700 : 500, fontSize: '12px', color: topColor || '#0f172a' }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: bottomColor || mutedColor || '#64748b', marginTop: '1px' }}>{bottom}</div>
    ) : null}
  </div>
);

const activityStatusPresentation = (statusRaw, isDarkMode) => {
  const s = String(statusRaw || '')
    .trim()
    .toLowerCase();
  const base = { label: statusRaw && String(statusRaw).trim() ? String(statusRaw).trim() : '—' };
  if (s === 'completa' || s === 'completada' || s === 'done') {
    return {
      ...base,
      bg: isDarkMode ? '#14532d' : '#dcfce7',
      color: isDarkMode ? '#bbf7d0' : '#166534',
      border: isDarkMode ? '#22c55e' : '#86efac',
    };
  }
  if (s === 'pendiente') {
    return {
      ...base,
      bg: isDarkMode ? '#713f12' : '#fef9c3',
      color: isDarkMode ? '#fde68a' : '#854d0e',
      border: isDarkMode ? '#eab308' : '#fde68a',
    };
  }
  if (s === 'vencida' || s === 'cancelada') {
    return {
      ...base,
      bg: isDarkMode ? '#7f1d1d' : '#fee2e2',
      color: isDarkMode ? '#fca5a5' : '#991b1b',
      border: isDarkMode ? '#ef4444' : '#fecaca',
    };
  }
  return {
    ...base,
    bg: isDarkMode ? '#27272a' : '#f0ebe3',
    color: isDarkMode ? '#e4e4e7' : '#44403c',
    border: isDarkMode ? '#3f3f46' : '#d6d0c4',
  };
};

const TABLE_COLUMNS = [
  { key: 'Fecha', align: 'left', sortKey: 'activity_date' },
  { key: 'Contacto', align: 'left', sortKey: 'contact_name' },
  { key: 'Acción', align: 'left', sortKey: 'action' },
  { key: 'Etapa funnel', align: 'center', sortKey: 'funnel_stage' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
  { key: 'Respuesta', align: 'left', sortKey: 'notes' },
  { key: 'Próximo paso', align: 'left', sortKey: 'next_step' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'activity_date':
      return String(row.activity_date || '');
    case 'contact_name':
      return String(row.contact_name || '').toLowerCase();
    case 'action':
      return String(row.action || '').toLowerCase();
    case 'funnel_stage':
      return String(row.funnel_stage || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    case 'notes':
      return String(row.notes || '').toLowerCase();
    case 'next_step':
      return String(row.next_step || row.next_step_date || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de actividades de contacto: mismo look que Table3 / ContactsTable3.
 */
const ContactActivitiesTable3 = ({
  rows = [],
  contacts = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Actividades',
  addButtonLabel = 'Nueva actividad',
  subtitleOverride,
  contactFilterId = '',
  onChangeContactFilter,
  onAdd,
  onRowNameClick,
  onBulkDelete,
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortColumn, setSortColumn] = useState('activity_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete);
  const colCount = TABLE_COLUMNS.length + (showBulkChrome ? 1 : 0);

  const contactOptions = useMemo(() => {
    if (Array.isArray(contacts) && contacts.length) {
      return contacts.map((c) => ({
        value: String(c.id),
        label: c.full_name || c.contact_code || `CTC ${c.id}`,
      }));
    }
    const map = new Map();
    rows.forEach((r) => {
      const id = String(r.contact_id || '');
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, r.contact_name || `CTC ${id}`);
      }
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [contacts, rows]);

  const statusOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const st = String(r.status || '').trim();
      if (st) map.set(st, st);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (contactFilterId) {
      list = list.filter((row) => {
        if (Array.isArray(row.contact_ids) && row.contact_ids.length) {
          return row.contact_ids.some((id) => String(id) === String(contactFilterId));
        }
        return String(row.contact_id) === String(contactFilterId);
      });
    }
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.activity_date,
          row.contact_name,
          ...(Array.isArray(row.contact_names) ? row.contact_names : []),
          row.owner_name,
          row.action,
          row.status,
          row.response,
          row.no_response ? 'sin respuesta' : '',
          row.notes,
          row.next_step,
          row.next_step_detail,
          row.next_step_date,
          row.follow_up_reminder_date,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) {
      list = list.filter((row) => String(row.status || '').trim() === filterStatus);
    }
    return list;
  }, [rows, contactFilterId, searchQuery, filterStatus]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const allVisibleSelected =
    sortedRows.length > 0 && sortedRows.every((row) => selectedIds.includes(Number(row.id)));
  const someVisibleSelected = sortedRows.some((row) => selectedIds.includes(Number(row.id)));

  React.useEffect(() => {
    if (headerSelectRef.current) {
      headerSelectRef.current.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visible = new Set(sortedRows.map((r) => Number(r.id)));
      setSelectedIds((prev) => prev.filter((id) => !visible.has(id)));
    } else {
      const visibleIds = sortedRows.map((r) => Number(r.id));
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const toggleSelectOne = (id) => {
    const nid = Number(id);
    setSelectedIds((prev) => (prev.includes(nid) ? prev.filter((x) => x !== nid) : [...prev, nid]));
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Eliminar ${n} actividad${n === 1 ? '' : 'es'} seleccionada${n === 1 ? '' : 's'}?`
    );
    if (!ok) return;
    setBulkPending(true);
    setBulkError('');
    try {
      await onBulkDelete(selectedIds);
      setSelectedIds([]);
    } catch (e) {
      setBulkError(e?.message || 'No se pudo eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir(key === 'activity_date' ? 'desc' : 'asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    onChangeContactFilter?.('');
  };

  const hasActiveFilters =
    Boolean(searchQuery.trim()) || Boolean(filterStatus) || Boolean(contactFilterId);

  const subtitle =
    subtitleOverride != null
      ? subtitleOverride
      : loading
        ? 'Cargando…'
        : error ||
          (hasActiveFilters
            ? `Mostrando ${sortedRows.length} de ${rows.length} actividades`
            : `${rows.length} actividades`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  const truncate = (value, max = 72) => {
    const s = String(value || '').trim();
    if (!s) return '—';
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> {addButtonLabel}
          </button>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Contacto, acción, notas, próximo paso…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Contacto
          </span>
          <select
            value={contactFilterId}
            onChange={(e) => onChangeContactFilter?.(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todos</option>
            {contactOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionada{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            disabled={bulkPending}
            onClick={handleBulkDelete}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#dc2626',
              color: '#fff',
              fontWeight: 600,
              fontSize: '12px',
              cursor: bulkPending ? 'wait' : 'pointer',
              opacity: bulkPending ? 0.7 : 1,
            }}
          >
            <Trash2 size={14} /> Eliminar
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todas"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando actividades…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay actividades para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const statusStyle = activityStatusPresentation(row.status, isDarkMode);
                const selected = selectedIds.includes(Number(row.id));
                return (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      background: selected ? rowHover : bg,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected ? rowHover : bg;
                    }}
                  >
                    {showBulkChrome ? (
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          aria-label={`Seleccionar actividad ${row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      {onRowNameClick ? (
                        <button
                          type="button"
                          onClick={() => onRowNameClick(row)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          <Stacked
                            top={row.activity_date || '—'}
                            bottom={row.owner_name || null}
                            topColor={ui.link || '#2563eb'}
                            mutedColor={muted}
                          />
                        </button>
                      ) : (
                        <Stacked
                          top={row.activity_date || '—'}
                          bottom={row.owner_name || null}
                          topColor={text}
                          mutedColor={muted}
                        />
                      )}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <Stacked
                        top={row.contact_name || 'Sin contacto'}
                        bottom={null}
                        topColor={text}
                        mutedColor={muted}
                      />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>
                      {row.action || '—'}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      {(() => {
                        const stageStyle = funnelStagePresentation(row.funnel_stage, isDarkMode);
                        return (
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: 700,
                              color: stageStyle.color,
                              background: stageStyle.bg,
                              border: `1px solid ${stageStyle.border}`,
                            }}
                          >
                            {stageStyle.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          border: `1px solid ${statusStyle.border}`,
                        }}
                      >
                        {statusStyle.label}
                      </span>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted, maxWidth: 220 }}>
                      {row.no_response
                        ? 'Sin Respuesta'
                        : truncate(row.response || row.notes, 80)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>
                      <Stacked
                        top={row.next_step || (row.has_follow_up_reminder ? 'Recordatorio' : '—')}
                        bottom={
                          [
                            row.next_step_date,
                            row.has_follow_up_reminder && row.follow_up_reminder_date
                              ? `Recordatorio ${row.follow_up_reminder_date}`
                              : null,
                            truncate(row.next_step_detail, 40),
                          ]
                            .filter((x) => x && x !== '—')
                            .join(' · ') || null
                        }
                        topColor={text}
                        mutedColor={muted}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContactActivitiesTable3;
